import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import { businessApi, claimsApi } from "../../services/api";

/** Same business id as other business APIs: from profile PK, not auth user.PK (USER#… vs BUSINESS#…). */
const businessIdFromProfile = (profile) => {
  if (!profile) return null;
  let raw = profile.PK ?? profile.business_id;
  if (raw == null || raw === "") return null;
  let id = String(raw);
  if (id.startsWith("BUSINESS#")) id = id.slice("BUSINESS#".length);
  return id || null;
};

const businessNameFromProfile = (profile) => {
  if (!profile) return null;
  const rawName = profile.name || "";
  if (!rawName) return null;
  const cleanName = String(rawName).trim();
  return cleanName || null;
};

/** Normalize beer_record from claim API for the success card (camelCase + snake_case). */
const successDisplayFromBeerRecord = (br) => {
  if (!br) return null;
  const recipientName = String(
    br.recipientName ?? br.recipient_name ?? "",
  ).trim();
  const city = String(br.recipientCity ?? br.recipient_city ?? "").trim();
  const state = String(br.recipientState ?? br.recipient_state ?? "").trim();
  let location = "";
  if (city && state) location = `${city}, ${state}`;
  else location = [city, state].filter(Boolean).join(" ");
  const partnerBrewery = String(br.brand ?? "").trim();
  const phone = String(
    br.recipientPhone ??
      br.recipient_phone ??
      br.recipientPhoneNumber ??
      br.phone ??
      "",
  ).trim();
  const rawMessage = String(
    br.personalMessage ?? br.personal_message ?? "",
  ).trim();
  const message = rawMessage ? `\u201c${rawMessage}\u201d` : "";
  const promoRaw = br.promoCode ?? br.promo_code;
  const promo =
    promoRaw != null && String(promoRaw).trim() !== ""
      ? String(promoRaw).trim()
      : null;

  return {
    recipientName: recipientName || null,
    location: location.trim() || null,
    partnerBrewery: partnerBrewery || null,
    phone: phone || null,
    message: message || null,
    promoCode: promo,
  };
};

const ClaimBeer = () => {
  const navigate = useNavigate();
  const { user, tokens } = useAuth();
  const [shortCode, setShortCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  const [businessName, setBusinessName] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [successSummary, setSuccessSummary] = useState(null);

  const accessToken = tokens?.access_token;

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryShortCode = searchParams.get("shortCode");
    if (queryShortCode) {
      setShortCode(queryShortCode.trim());
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!accessToken) {
        setProfileLoading(false);
        setBusinessId(null);
        setBusinessName(null);
        return;
      }

      setProfileLoading(true);
      try {
        const profile = await businessApi.getMyProfile(accessToken);
        if (cancelled) return;
        const id = businessIdFromProfile(profile);
        const name = businessNameFromProfile(profile);
        setBusinessId(id);
        setBusinessName(name);
        if (!id) {
          toast.error(
            "Your business profile is missing an ID. Please complete your profile first.",
            { position: "top-right", autoClose: 5000 },
          );
        }
      } catch (err) {
        if (cancelled) return;
        setBusinessId(null);
        setBusinessName(null);
        const msg =
          (typeof err?.error === "string" && err.error) ||
          err?.message ||
          "Failed to load business profile";
        toast.error(msg, { position: "top-right", autoClose: 5000 });
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const claimedBy = useMemo(() => {
    if (user?.PK) return user.PK.replace(/^USER#/, "");
    return null;
  }, [user]);

  const handleConfirm = async () => {
    if (!shortCode.trim()) {
      toast.error("Please enter a short code.", { position: "top-right" });
      return;
    }
    if (!accessToken) {
      toast.error("Authentication expired. Please log in again.", {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }
    if (!businessId) {
      toast.error(
        "Your business profile is missing an ID. Please complete your profile first.",
        { position: "top-right", autoClose: 5000 },
      );
      return;
    }
    if (!claimedBy) {
      toast.error("Unable to get user ID for claiming.", {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    try {
      setLoading(true);
      const claimResponse = await claimsApi.claimBeerRecord(
        {
          short_code: shortCode.trim(),
          claimed_by: claimedBy,
          business_id: businessId,
        },
        accessToken,
      );

      const beerRecord =
        claimResponse?.beer_record || claimResponse?.beerRecord;
      const beerId =
        beerRecord?.beerId || beerRecord?.beer_id || beerRecord?.beerID;
      const recipientPhone =
        beerRecord?.recipient_phone ||
        beerRecord?.recipientPhone ||
        beerRecord?.recipientPhoneNumber ||
        beerRecord?.phone ||
        "";
      const claimId = beerRecord?.claimId || beerRecord?.claim_id;

      if (!beerId) {
        toast.error(
          "Beer record returned from claim but beerId is missing; cannot create ledger entry.",
          { position: "top-right", autoClose: 5000 },
        );
        return;
      }

      await claimsApi.createBeerClaimEntry(
        {
          business_id: businessId,
          beer_id: beerId,
          claimed_by: claimedBy,
          short_code: shortCode.trim(),
          status: "claimed",
          recipient_phone: recipientPhone,
          ...(claimId ? { claim_id: claimId } : {}),
        },
        accessToken,
      );

      setSuccessSummary(successDisplayFromBeerRecord(beerRecord));
    } catch (err) {
      const errorMessage =
        err?.error ||
        err?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to claim beer.";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const dash = "\u2014";

  const successRows = successSummary
    ? [
        { label: "Recipient Name:", value: successSummary.recipientName },
        // { label: "Location:", value: successSummary.location },
        { label: "Partner:", value: successSummary.partnerBrewery },
        { label: "Phone:", value: successSummary.phone },
        // { label: "Message:", value: successSummary.message },
        // {
        //   label: "Promo Code:",
        //   value:
        //     successSummary.promoCode != null && successSummary.promoCode !== ""
        //       ? successSummary.promoCode
        //       : null,
        // },
      ]
    : [];

  const instructionSteps = [
    {
      title: "Scan the QR Code",
      body: "Use the POS scanner or your mobile phone to scan the QR code provided by the vendor.",
    },
    {
      title: "Automatic Verification",
      body: "Once scanned, the claim will be processed automatically and added to your account.",
    },
    {
      title: "Manual Entry (If Scan Fails)",
      body: "If the QR code cannot be scanned, enter the manual code provided into the field below and click Enter Code.",
    },
    {
      title: "Confirmation",
      body: "You'll receive an on-screen confirmation once the claim is successfully recorded.",
    },
  ];

  if (successSummary) {
    return (
      <div
        style={{
          maxWidth: "560px",
          margin: "0 auto",
          padding: "24px 16px 40px",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            margin: "0 0 28px",
            fontSize: "28px",
            fontWeight: "700",
            color: "#DE810E",
            letterSpacing: "-0.02em",
          }}
        >
          Scan Successful!
        </h2>
        <div
          style={{
            textAlign: "left",
            background: "#faf6ef",
            borderRadius: "14px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
            padding: "28px 32px 32px",
          }}
        >
          {successRows.map((row) => (
            <div key={row.label} style={{ marginBottom: "22px" }}>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#2c2c2c",
                  marginBottom: "6px",
                }}
              >
                {row.label}
              </div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: 400,
                  color: "#6b6b6b",
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
              >
                {row.value != null && row.value !== "" ? row.value : dash}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "12px",
            marginTop: "28px",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setSuccessSummary(null);
              setShortCode("");
            }}
            style={{
              minHeight: "46px",
              padding: "0 24px",
              borderRadius: "999px",
              border: "none",
              cursor: "pointer",
              background: "#DE810E",
              color: "white",
              fontWeight: "700",
              fontSize: "15px",
            }}
          >
            Claim another
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin-dashboard")}
            style={{
              minHeight: "46px",
              padding: "0 24px",
              borderRadius: "999px",
              border: "2px solid #DE810E",
              cursor: "pointer",
              background: "white",
              color: "#DE810E",
              fontWeight: "700",
              fontSize: "15px",
            }}
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <h2
        style={{
          textAlign: "left",
          margin: "0 0 28px",
          fontSize: "30px",
          fontWeight: "700",
          color: "#000000",
        }}
      >
        Claim Beer
      </h2>

      <h3
        style={{
          margin: "0 0 16px",
          fontSize: "16px",
          fontWeight: "700",
          color: "#000000",
        }}
      >
        How to Claim Beer
      </h3>

      <ol
        style={{
          margin: "0 0 36px",
          paddingLeft: "22px",
          color: "#000000",
          fontSize: "15px",
          lineHeight: 1.55,
          maxWidth: "100%",
        }}
      >
        {instructionSteps.map((step) => (
          <li key={step.title} style={{ marginBottom: "14px" }}>
            <strong>{step.title}</strong>
            <br />
            <span style={{ fontWeight: 400 }}>{step.body}</span>
          </li>
        ))}
      </ol>

      <p
        style={{
          margin: "0 0 28px",
          fontSize: "18px",
          fontWeight: "700",
          color: "#DE810E",
          lineHeight: 1.45,
          maxWidth: "640px",
        }}
      >
        Ready to Scan – Please scan the QR code using the POS or mobile
        device...
      </p>

      <p
        style={{
          margin: "0 0 10px",
          fontSize: "14px",
          fontWeight: 400,
          color: "#000000",
        }}
      >
        or enter manual code instead..
      </p>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "stretch",
          gap: "12px",
          marginBottom: "24px",
          maxWidth: "640px",
        }}
      >
        <input
          type="text"
          value={shortCode}
          onChange={(e) => setShortCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleConfirm();
            }
          }}
          placeholder="Enter Manual Code.."
          aria-label="Manual claim code"
          style={{
            flex: "1 1 200px",
            minWidth: 0,
            padding: "12px 14px",
            borderRadius: "6px",
            border: "1px solid #d0d0d0",
            fontSize: "15px",
          }}
        />
        <button
          type="button"
          style={{
            flex: "0 0 auto",
            minHeight: "46px",
            padding: "0 28px",
            borderRadius: "999px",
            border: "none",
            cursor: loading || profileLoading ? "not-allowed" : "pointer",
            background: "#DE810E",
            color: "white",
            fontWeight: "700",
            fontSize: "16px",
            opacity: loading || profileLoading ? 0.65 : 1,
          }}
          onClick={handleConfirm}
          disabled={loading || profileLoading}
        >
          {loading ? "Please wait…" : "Enter Code"}
        </button>
      </div>
    </>
  );
};

export default ClaimBeer;
